import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  useGetParentQuery, 
  useUpdateParentMutation 
} from "redux_dir/features/parentApi";
import { getParentForm } from "redux_dir/features/parentSlice";

/**
 * Hook personnalisé pour gérer la logique du profil utilisateur
 * @returns {Object} État et fonctions pour gérer le profil
 */
export const useProfileLogic = () => {
  const navigate = useNavigate();
  const parentForm = useSelector(getParentForm);
  const parentId = parentForm?.id;

  const [isEditing, setIsEditing] = useState(false);

  // Debug: Afficher les informations
  useEffect(() => {
    console.log("🔍 [ProfilePage Debug]");
    console.log("parentForm:", parentForm);
    console.log("parentId:", parentId);
  }, [parentForm, parentId]);

  // Récupérer les données du parent
  const { 
    data: parentDataResponse, 
    isLoading: isLoadingParent,
    error: loadError,
    isSuccess,
  } = useGetParentQuery(parentId, {
    skip: !parentId,
  });

  // Debug: Afficher la réponse de l'API
  useEffect(() => {
    console.log("📡 [API Response]");
    console.log("parentDataResponse:", parentDataResponse);
    console.log("isLoading:", isLoadingParent);
    console.log("isSuccess:", isSuccess);
    console.log("error:", loadError);
  }, [parentDataResponse, isLoadingParent, isSuccess, loadError]);

  // Extraire les données (peut être dans data.data ou directement data)
  const parentData = parentDataResponse?.data || parentDataResponse || parentForm;

  // Debug: Afficher les données finales
  useEffect(() => {
    console.log("💾 [Final Data] parentData:", parentData);
  }, [parentData]);

  const [updateParent, { isLoading: isUpdating }] = useUpdateParentMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      fullName: "",
      phone: "",
      adresse: "",
      villeId: "",
    },
  });

  // Mettre à jour les valeurs par défaut quand les données arrivent
  useEffect(() => {
    if (parentData) {
      console.log("✅ [Form Reset] Updating form with:", parentData);
      reset({
        fullName: parentData.fullName || "",
        phone: parentData.phone || "",
        adresse: parentData.adresse || "",
        villeId: parentData.villeId || parentData.ville?.id || "",
      });
    }
  }, [parentData, reset]);

  /**
   * Soumettre les modifications du profil
   */
  const onSubmit = async (data) => {
    try {
      await updateParent({
        parentId,
        ...data,
      }).unwrap();

      toast.success("Profil mis à jour avec succès !");
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Erreur lors de la mise à jour du profil");
    }
  };

  /**
   * Annuler les modifications
   */
  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  /**
   * Activer le mode édition
   */
  const handleEdit = () => {
    setIsEditing(true);
  };

  /**
   * Naviguer vers une page
   */
  const handleNavigate = (path) => {
    navigate(path);
  };

  /**
   * Retour à la page précédente
   */
  const handleBack = () => {
    navigate(-1);
  };

  return {
    // État
    parentData,
    parentForm,
    isLoadingParent,
    isUpdating,
    isEditing,
    errors,
    loadError,
    
    // Fonctions de formulaire
    register,
    handleSubmit,
    onSubmit,
    
    // Actions
    handleEdit,
    handleCancel,
    handleNavigate,
    handleBack,
  };
};
